import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoIcon,
  Psychology as AIIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import autoApprovalSettingsService from '../../services/autoApprovalSettingsService';

const AutoApprovalSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    min_ai_recommendation: 95.00,
    min_ai_confidence: 95.00,
    manual_review_categories: '',
    log_auto_approvals: true,
    send_daily_reports: true,
    admin_email_recipients: '',
  });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [metadata, setMetadata] = useState({
    updated_at: null,
    updated_by: null,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await autoApprovalSettingsService.getSettings();
      
      if (response.success && response.settings) {
        setSettings(response.settings);
        setMetadata({
          updated_at: response.settings.updated_at,
          updated_by: response.settings.updated_by,
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showAlert('Failed to load auto-approval settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validate thresholds
      if (settings.min_ai_recommendation < 0 || settings.min_ai_recommendation > 100) {
        showAlert('AI Recommendation threshold must be between 0 and 100', 'error');
        return;
      }
      
      if (settings.min_ai_confidence < 0 || settings.min_ai_confidence > 100) {
        showAlert('AI Confidence threshold must be between 0 and 100', 'error');
        return;
      }

      const response = await autoApprovalSettingsService.updateSettings(settings);
      
      if (response.success) {
        setSettings(response.settings);
        setMetadata({
          updated_at: response.settings.updated_at,
          updated_by: response.settings.updated_by,
        });
        showAlert('Auto-approval settings updated successfully!', 'success');
      } else {
        showAlert(response.error || 'Failed to update settings', 'error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save auto-approval settings';
      showAlert(errorMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => {
      setAlert({ show: false, message: '', severity: 'success' });
    }, 5000);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <AutoIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Auto-Approval Settings
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Configure automatic product approval based on AI moderation scores
        </Typography>
      </Box>

      {/* Alert */}
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 3 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      {/* Metadata */}
      {metadata.updated_at && (
        <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              Last updated: {new Date(metadata.updated_at).toLocaleString()}
              {metadata.updated_by && ` by ${metadata.updated_by}`}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Main Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <SecurityIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            General Settings
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Enable/Disable Auto-Approval */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ bgcolor: settings.enabled ? 'success.light' : 'grey.100' }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enabled}
                      onChange={(e) => handleFieldChange('enabled', e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        Enable Auto-Approval System
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {settings.enabled
                          ? 'Products with high AI scores will be automatically approved'
                          : 'All products will require manual review'}
                      </Typography>
                    </Box>
                  }
                />
                {settings.enabled && (
                  <Chip
                    icon={<CheckIcon />}
                    label="ACTIVE"
                    color="success"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* AI Thresholds */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="AI Recommendation Threshold"
              type="number"
              value={settings.min_ai_recommendation}
              onChange={(e) => handleFieldChange('min_ai_recommendation', parseFloat(e.target.value))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><AIIcon /></InputAdornment>,
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText="Minimum AI recommendation score for auto-approval (0-100)"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="AI Confidence Threshold"
              type="number"
              value={settings.min_ai_confidence}
              onChange={(e) => handleFieldChange('min_ai_confidence', parseFloat(e.target.value))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><AIIcon /></InputAdornment>,
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText="Minimum AI confidence score for auto-approval (0-100)"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Category Restrictions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <SecurityIcon color="warning" />
          <Typography variant="h6" fontWeight="bold">
            Manual Review Categories
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Categories Requiring Manual Review"
          value={settings.manual_review_categories}
          onChange={(e) => handleFieldChange('manual_review_categories', e.target.value)}
          placeholder="e.g., Electronics, Health & Beauty, Pharmaceuticals (comma-separated category SIDs)"
          helperText="Enter comma-separated category SIDs that should always require manual review, regardless of AI scores"
        />
      </Paper>

      {/* Logging & Reports */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <EmailIcon color="info" />
          <Typography variant="h6" fontWeight="bold">
            Logging & Reporting
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.log_auto_approvals}
                  onChange={(e) => handleFieldChange('log_auto_approvals', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Log Auto-Approvals
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Keep audit trail of all auto-approved products
                  </Typography>
                </Box>
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.send_daily_reports}
                  onChange={(e) => handleFieldChange('send_daily_reports', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Send Daily Reports
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Email daily summary of auto-approved products
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {settings.send_daily_reports && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Admin Email Recipients"
                value={settings.admin_email_recipients}
                onChange={(e) => handleFieldChange('admin_email_recipients', e.target.value)}
                placeholder="admin1@example.com, admin2@example.com"
                helperText="Comma-separated list of email addresses to receive daily reports"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment>,
                }}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box display="flex" gap={2} justifyContent="flex-end">
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSettings}
          disabled={saving}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Container>
  );
};

export default AutoApprovalSettings;

